import { useRef, useEffect, useCallback, useMemo, useState } from 'react';

import type { MapMove, MapPing, HoveredPing } from '../types';
import type { MoveOverlaySet, MapProjectionHandle } from '../overlays/types';
import { createProjectionOverlay } from '../overlays/createProjectionOverlay';
import { createRouteOverlays } from '../overlays/createRouteOverlays';
import { createStopOverlays } from '../overlays/createStopOverlays';
import type { ClickedStop } from '../overlays/createStopOverlays';
import { createPingOverlays, createBreadcrumbDots } from '../overlays/createPingOverlays';

// OPTIONS ------------------------------------------------------------------- //

interface UseMoveOverlaysOptions {
  map: google.maps.Map | null;
  moves: MapMove[];
  pingsByMoveId: Record<number, MapPing[]>;
  unassignedPings: MapPing[];
  highlightedMoveId: number | null;
  selectedDriverId: number | null;
  showGeofences?: boolean;
  onHoverPing?: (ping: HoveredPing | null) => void;
  onClickStop?: (stop: ClickedStop | null) => void;
}

// HOOK ---------------------------------------------------------------------- //

export function useMoveOverlays({
  map,
  moves,
  pingsByMoveId,
  unassignedPings,
  highlightedMoveId,
  selectedDriverId,
  showGeofences = true,
  onHoverPing,
  onClickStop,
}: UseMoveOverlaysOptions) {
  const overlaysByMoveRef = useRef<Map<number, MoveOverlaySet>>(new Map());
  const betweenMoveDotsRef = useRef<google.maps.Marker[]>([]);
  const animationFrameIdsRef = useRef<number[]>([]);
  const hoverListenersRef = useRef<google.maps.MapsEventListener[]>([]);
  const clickListenersRef = useRef<google.maps.MapsEventListener[]>([]);
  const projectionRef = useRef<MapProjectionHandle | null>(null);

  // Bumped after every overlay rebuild so the highlight effect re-applies
  const [overlayVersion, setOverlayVersion] = useState(0);

  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  // ---- PROJECTION OVERLAY ---- //

  useEffect(() => {
    if (!map) return;
    const handle = createProjectionOverlay(map);
    projectionRef.current = handle;
    return () => {
      handle.cleanup();
      projectionRef.current = null;
    };
  }, [map]);

  // ---- CLEANUP ---- //

  const clearAllOverlays = useCallback(() => {
    animationFrameIdsRef.current.forEach(id => cancelAnimationFrame(id));
    animationFrameIdsRef.current = [];

    hoverListenersRef.current.forEach(l => google.maps.event.removeListener(l));
    hoverListenersRef.current = [];

    clickListenersRef.current.forEach(l => google.maps.event.removeListener(l));
    clickListenersRef.current = [];

    overlaysByMoveRef.current.forEach(overlay => {
      overlay.routeGlow?.setMap(null);
      overlay.routeMain?.setMap(null);
      overlay.routeAnimated?.setMap(null);
      overlay.geofences.forEach(g => g.setMap(null));
      overlay.pickupMarker?.setMap(null);
      overlay.deliveryMarker?.setMap(null);
      overlay.deviationLines.forEach(l => l.setMap(null));
      overlay.deviationDots.forEach(d => d.setMap(null));
      overlay.routePingMarkers.forEach(m => m.setMap(null));
    });
    overlaysByMoveRef.current.clear();

    betweenMoveDotsRef.current.forEach(d => d.setMap(null));
    betweenMoveDotsRef.current = [];
  }, []);

  // ---- HIDE GEOFENCES IN SATELLITE VIEW ---- //

  // Ref for showGeofences so the zoom listener can read the latest value
  const showGeofencesRef = useRef(showGeofences);
  showGeofencesRef.current = showGeofences;

  useEffect(() => {
    if (!map) return;
    const SATELLITE_ZOOM = 19;

    const updateGeofenceVisibility = () => {
      const zoom = map.getZoom() ?? 10;
      const visible = zoom < SATELLITE_ZOOM && showGeofencesRef.current;
      overlaysByMoveRef.current.forEach(overlay => {
        overlay.geofences.forEach(g => g.setVisible(visible));
      });
    };

    const listener = map.addListener('zoom_changed', updateGeofenceVisibility);
    return () => google.maps.event.removeListener(listener);
  }, [map]);

  // ---- BUILD OVERLAYS ---- //

  useEffect(() => {
    if (!map || !selectedDriverId || moves.length === 0) {
      clearAllOverlays();
      return;
    }

    clearAllOverlays();

    // 1. Routes
    const { results: routeResults, animationFrameIds } = createRouteOverlays({
      map,
      moves,
      prefersReducedMotion,
    });
    animationFrameIdsRef.current = animationFrameIds;

    // Track rendered geofence location IDs to deduplicate
    const renderedLocationIds = new Set<number>();

    // 2. Stops + Pings per move
    routeResults.forEach(({ overlaySet, routePath, status }) => {
      const moveId = overlaySet.moveId!;

      // Stops
      const move = moves.find(m => m.id === moveId)!;
      const isConsumerMove = move.consumer_type === 'customer' || move.consumer_type === 'loaner';
      const stops = createStopOverlays({
        map,
        pickup: move.lane?.pickup,
        delivery: move.lane?.delivery,
        renderedLocationIds,
        consumer: isConsumerMove ? {
          consumerName: move.consumer_name,
          consumerPhone: move.consumer_phone,
          consumerAtPickup: move.consumer_at_pickup,
        } : undefined,
        onClickStop,
        clickListeners: clickListenersRef.current,
      });

      // Pings (pre-indexed — no per-move full-array filtering)
      const movePings = pingsByMoveId[moveId] || [];
      const pings = createPingOverlays({
        map,
        movePings,
        moveId,
        routePath,
        projectionHandle: projectionRef.current,
        onHoverPing,
        hoverListeners: hoverListenersRef.current,
        onRouteWindow: {
          start: move.pickup_successful || undefined,
          end: move.delivery_successful || undefined,
        },
      });

      const fullSet: MoveOverlaySet = {
        moveId,
        status,
        routeGlow: overlaySet.routeGlow ?? null,
        routeMain: overlaySet.routeMain ?? null,
        routeAnimated: overlaySet.routeAnimated ?? null,
        geofences: stops.geofences,
        pickupMarker: stops.pickupMarker,
        deliveryMarker: stops.deliveryMarker,
        deviationLines: pings.deviationLines,
        deviationDots: pings.deviationDots,
        routePingMarkers: pings.routePingMarkers,
      };

      overlaysByMoveRef.current.set(moveId, fullSet);
    });

    // 3. Between-move breadcrumb dots
    betweenMoveDotsRef.current = createBreadcrumbDots({
      map,
      unassignedPings,
      projectionHandle: projectionRef.current,
      onHoverPing,
      hoverListeners: hoverListenersRef.current,
    });

    // Trigger highlight effect to re-apply to newly created overlays
    setOverlayVersion(v => v + 1);

    return () => {
      clearAllOverlays();
    };
  }, [map, selectedDriverId, moves, pingsByMoveId, unassignedPings, clearAllOverlays, onHoverPing, onClickStop, prefersReducedMotion]);

  // ---- HIGHLIGHT TOGGLING (no teardown) ---- //

  useEffect(() => {
    const hasFocus = highlightedMoveId !== null;

    overlaysByMoveRef.current.forEach((overlay, moveId) => {
      const isSelected = highlightedMoveId === moveId;
      const isVisible = !hasFocus || isSelected;
      const isFuture = overlay.status === 'future';

      if (overlay.routeGlow) {
        overlay.routeGlow.setVisible(isVisible);
        if (isVisible) overlay.routeGlow.setOptions({ strokeOpacity: isFuture ? 0.2 : 0.25 });
      }
      if (overlay.routeMain) {
        overlay.routeMain.setVisible(isVisible);
        if (isVisible) overlay.routeMain.setOptions({ strokeOpacity: isFuture ? 0.8 : 1 });
      }
      if (overlay.routeAnimated) {
        overlay.routeAnimated.setVisible(isVisible);
      }

      overlay.geofences.forEach(g => {
        const geofenceVisible = isVisible && showGeofences;
        if (g instanceof google.maps.Circle) {
          g.setOptions({ fillOpacity: 0.25, strokeOpacity: 0.9 });
        } else {
          (g as google.maps.Polygon).setOptions({ fillOpacity: 0.25, strokeOpacity: 0.9 });
        }
        g.setVisible(geofenceVisible);
      });

      overlay.pickupMarker?.setVisible(isVisible);
      overlay.deliveryMarker?.setVisible(isVisible);
      overlay.deviationLines.forEach(l => l.setVisible(isVisible));
      overlay.deviationDots.forEach(d => d.setVisible(isVisible));
      overlay.routePingMarkers.forEach(m => m.setVisible(isVisible));
    });

    const showBreadcrumbs = !hasFocus;
    betweenMoveDotsRef.current.forEach(d => d.setVisible(showBreadcrumbs));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedMoveId, showGeofences, overlayVersion]);

  // ---- CLEANUP ON UNMOUNT ---- //

  useEffect(() => {
    return () => {
      clearAllOverlays();
    };
  }, [clearAllOverlays]);
}
